import { useEffect, useMemo, useRef, useState } from "react";
import { Plus, Tag as TagIcon } from "lucide-react";

import { TagChip } from "@/components/alia/tag-chip";
import { cn } from "@/lib/utils";
import type { Tag } from "@/types";

interface TagPickerProps {
  /** Tags currently on this conversation. */
  selected: Tag[];
  /** All tags that exist in the tenant (used for autocomplete). */
  availableLabels: string[];
  onAdd: (label: string) => void;
  onRemove: (tagId: string) => void;
}

/**
 * Inline tag editor: shows current tags + a "+ Tag" button that expands
 * into an input with autocomplete suggestions. Pressing Enter creates a
 * new tag if the typed label doesn't exist yet.
 */
export function TagPicker({
  selected,
  availableLabels,
  onAdd,
  onRemove,
}: TagPickerProps) {
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState("");
  const inputRef = useRef<HTMLInputElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  // Focus input on open
  useEffect(() => {
    if (open) {
      requestAnimationFrame(() => inputRef.current?.focus());
    } else {
      setDraft("");
    }
  }, [open]);

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    function onClick(e: MouseEvent) {
      if (!containerRef.current?.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    window.addEventListener("mousedown", onClick);
    return () => window.removeEventListener("mousedown", onClick);
  }, [open]);

  const selectedLabels = new Set(selected.map((t) => t.label.toLowerCase()));

  const suggestions = useMemo(() => {
    const q = draft.trim().toLowerCase();
    return availableLabels
      .filter((label) => !selectedLabels.has(label.toLowerCase()))
      .filter((label) => (q ? label.toLowerCase().includes(q) : true))
      .slice(0, 6);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [draft, availableLabels, selected]);

  const trimmed = draft.trim();
  const isNewTag =
    trimmed.length > 0 &&
    !availableLabels.some((l) => l.toLowerCase() === trimmed.toLowerCase()) &&
    !selectedLabels.has(trimmed.toLowerCase());

  function handleAdd(label: string) {
    const clean = label.trim().toLowerCase();
    if (!clean) return;
    if (selectedLabels.has(clean)) return;
    onAdd(clean);
    setDraft("");
    inputRef.current?.focus();
  }

  return (
    <div ref={containerRef} className="relative">
      <div className="flex flex-wrap items-center gap-1.5">
        {selected.map((tag) => (
          <TagChip
            key={tag.id}
            tag={tag}
            onRemove={() => onRemove(tag.id)}
          />
        ))}

        <button
          onClick={() => setOpen((v) => !v)}
          className={cn(
            "inline-flex items-center gap-1 rounded-full border border-dashed px-2 py-0.5 text-[11px] font-medium transition-colors",
            open
              ? "border-brand/40 bg-brand/10 text-brand"
              : "border-border text-muted-foreground hover:border-border-strong hover:text-foreground"
          )}
        >
          <Plus className="size-3" />
          {selected.length === 0 ? "Agregar tag" : "Tag"}
        </button>
      </div>

      {open && (
        <div className="absolute left-0 top-full z-20 mt-2 w-64 overflow-hidden rounded-lg border border-border-strong bg-surface shadow-2xl ring-1 ring-white/5 animate-slide-up">
          <div className="border-b border-border px-3 py-2">
            <div className="flex items-center gap-2">
              <TagIcon className="size-3.5 shrink-0 text-muted-foreground" />
              <input
                ref={inputRef}
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    if (trimmed) handleAdd(trimmed);
                  } else if (e.key === "Escape") {
                    e.preventDefault();
                    setOpen(false);
                  }
                }}
                placeholder="Buscar o crear…"
                className="h-7 flex-1 bg-transparent text-sm placeholder:text-muted-foreground focus:outline-none"
              />
            </div>
          </div>

          <div className="max-h-56 overflow-y-auto p-1">
            {/* "Create new" option */}
            {isNewTag && (
              <button
                onClick={() => handleAdd(trimmed)}
                className="flex w-full items-center justify-between gap-2 rounded-md px-2 py-1.5 text-left text-xs hover:bg-surface-2"
              >
                <span className="flex items-center gap-2">
                  <Plus className="size-3 text-brand" />
                  <span className="text-muted-foreground">Crear:</span>
                  <span className="font-medium text-foreground">
                    {trimmed.toLowerCase()}
                  </span>
                </span>
                <kbd className="rounded border border-border bg-surface-2 px-1 font-mono text-[9px]">
                  ↵
                </kbd>
              </button>
            )}

            {suggestions.length > 0 && (
              <>
                {isNewTag && (
                  <div className="my-1 px-2 text-[10px] uppercase tracking-wide text-muted-foreground">
                    Existentes
                  </div>
                )}
                {suggestions.map((label) => (
                  <button
                    key={label}
                    onClick={() => handleAdd(label)}
                    className="flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left text-xs hover:bg-surface-2"
                  >
                    <TagChip
                      tag={{
                        id: `preview-${label}`,
                        label,
                        source: "human",
                        createdAt: "",
                      }}
                      size="sm"
                    />
                  </button>
                ))}
              </>
            )}

            {!isNewTag && suggestions.length === 0 && (
              <div className="px-3 py-4 text-center text-xs text-muted-foreground">
                {draft.trim()
                  ? "Sin coincidencias"
                  : "Escribe para buscar o crear una tag"}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
